/**
 * Desktop spacing + column-width presets — the single source for
 * responsive rhythm across the app.
 *
 * Every named key is keyed by the four-tier breakpoint scale from
 * `breakpoints.ts`. Screens never hardcode `maxWidth: 960` or
 * `paddingHorizontal: 40` — they pull from here, keyed on the current
 * `bp` returned by `useBreakpoint()`.
 *
 * Column presets follow a readability scale lifted from print + web
 * precedent:
 *   - 480–520  form columns (auth, modals, narrow inputs)
 *   - 720      reading columns (lists, prose)
 *   - 800      content columns (mixed media, profile, trip detail)
 *   - 1120     dashboards (hosting, trips grid)
 *   - 1200     full app-wide content (explore with filters)
 *   - 1440     marketing hero
 */
import { SPACING } from '../brand'
import type { BreakpointKey } from './breakpoints'

type ResponsiveNumber = Record<BreakpointKey, number>

/**
 * Keyed by breakpoint. The rhythm scales up on wider viewports so content
 * doesn't feel cramped on a 1440 monitor the way mobile spacing would.
 */
export const DESKTOP_SPACING: {
  shellPaddingX: ResponsiveNumber
  shellPaddingY: ResponsiveNumber
  sectionGap: ResponsiveNumber
  cardGap: ResponsiveNumber
  pageTitleToBody: ResponsiveNumber
} = {
  shellPaddingX: {
    sm: SPACING.lg,
    md: SPACING.xl,
    lg: SPACING['2xl'],
    xl: SPACING['2xl'],
  },
  shellPaddingY: {
    sm: SPACING.lg,
    md: SPACING.xl,
    lg: SPACING['2xl'],
    xl: SPACING['2xl'],
  },
  sectionGap: {
    sm: SPACING.xl,
    md: SPACING.xl,
    lg: SPACING['2xl'],
    xl: SPACING['3xl'],
  },
  cardGap: {
    sm: SPACING.md,
    md: SPACING.md,
    lg: SPACING.lg,
    xl: SPACING.lg,
  },
  pageTitleToBody: {
    sm: SPACING.lg,
    md: SPACING.xl,
    lg: SPACING['2xl'],
    xl: SPACING['2xl'],
  },
} as const

/**
 * Named column widths. Always use a preset unless there is a very
 * specific reason. New screens that need a novel width must add the key
 * here — not inline — so the decision is visible and reusable.
 */
export const COLUMN_PRESETS = {
  form: 480,
  formWide: 520,
  /** Compact notification chip. Desktop/tablet toasts, not page columns. */
  toast: 380,
  reading: 720,
  content: 800,
  dashboard: 1120,
  app: 1200,
  marketing: 1440,
} as const

export type ColumnPreset = keyof typeof COLUMN_PRESETS

export function resolveColumnWidth(width: ColumnPreset | number): number {
  return typeof width === 'number' ? width : COLUMN_PRESETS[width]
}
